import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { 
  Search, 
  RefreshCw, 
  Mail, 
  Palette,
  Layers,
  ArrowUpDown,
  Clock,
  Loader,
  Filter
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorArtistsManagement.css';

const CuratorArtistsManagement = () => {
  const navigate = useNavigate();
  const { selectedOrganization } = useOutletContext() || {};
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [displayedCount, setDisplayedCount] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);
  const loaderRef = useRef(null);
  const [stats, setStats] = useState({
    total: 0,
    withArtworks: 0,
    withSubmissions: 0,
    totalCollections: 0
  });

  // Get current user
  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  };

  // Load all artists for curator's organization
  const loadArtists = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = await getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // If no organization selected, show empty state
      if (!selectedOrganization?.organization_id) {
        setArtists([]);
        setStats({ total: 0, withArtworks: 0, withSubmissions: 0, totalCollections: 0 });
        return;
      }

      // Parallel fetch: collections
      const [collectionsResult] = await Promise.all([
        // Get collections for the selected organization
        supabase
          .from('collections')
          .select('collection_id')
          .eq('organization_id', selectedOrganization.organization_id)
      ]);

      if (collectionsResult.error) {
        console.warn('Error fetching collections:', collectionsResult.error);
      }

      const collections = collectionsResult.data || [];
      const collectionIds = collections.map(c => c.collection_id);

      // If no collections, we have no artists to show
      let artistsFromCollections = [];
      let artworkCountMap = new Map();
      let mappings = [];

      if (collectionIds.length > 0) {
        // Get all unique artists from these collections with their artworks
        const { data: mappingsData, error: mappingsError } = await supabase
          .from('collection_vc_mapping')
          .select(`
            vc_id,
            collection_id,
            vcs!vc_id (
              vc_id,
              uuid,
              created_at
            )
          `)
          .in('collection_id', collectionIds);

        if (mappingsError) throw mappingsError;
        mappings = mappingsData || [];

        // Get unique artist UUIDs from collections
        const artistUuids = [...new Set(
          mappings
            .map(m => m.vcs?.uuid)
            .filter(Boolean)
        )];

        if (artistUuids.length > 0) {
          // Get artist details
          const { data: artistsData, error: artistsError } = await supabase
            .from('users')
            .select('*')
            .in('uuid', artistUuids);

          if (artistsError) throw artistsError;

          // Build artwork count map
          mappings.forEach(item => {
            const uuid = item.vcs?.uuid;
            if (uuid) {
              artworkCountMap.set(uuid, (artworkCountMap.get(uuid) || 0) + 1);
            }
          });

          // Enrich artist data with artwork counts
          artistsFromCollections = (artistsData || [])
            .map(artist => {
              return {
                uuid: artist.uuid,
                nickname: artist.nickname || `${artist.first_name || ''} ${artist.last_name || ''}`.trim() || 'Unknown',
                first_name: artist.first_name,
                last_name: artist.last_name,
                email: artist.email,
                profile_img: artist.profile_img,
                handle: artist.handle,
                created_at: artist.created_at,
                updated_at: artist.updated_at,
                artwork_count: artworkCountMap.get(artist.uuid) || 0,
                collections: [...new Set(
                  mappings
                    .filter(m => m.vcs?.uuid === artist.uuid)
                    .map(m => m.collection_id)
                )].length,
                isVibrantCastUser: true,
                source: 'vibrantcast'
              };
            })
            .filter(artist => artist.artwork_count > 0);
        }
      }

      // Combine all artists
      const allArtists = [
        ...artistsFromCollections
      ];

      setArtists(allArtists);

      // Calculate stats
      setStats({
        total: allArtists.length,
        withArtworks: allArtists.filter(a => a.artwork_count > 0).length,
        withSubmissions: artistsFromCollections.length,
        totalCollections: collectionIds.length
      });

    } catch (error) {
      console.error('Error loading artists:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('CuratorArtistsManagement: selectedOrganization changed:', selectedOrganization?.organization_id);
    loadArtists();
  }, [selectedOrganization?.organization_id]);

  // Filter and sort artists
  const filteredArtists = artists
    .filter(artist => {
      const searchLower = searchQuery.toLowerCase();
      return (
        artist.nickname.toLowerCase().includes(searchLower) ||
        artist.email.toLowerCase().includes(searchLower) ||
        (artist.handle && artist.handle.toLowerCase().includes(searchLower))
      );
    })
    .sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = a.nickname.toLowerCase();
          bValue = b.nickname.toLowerCase();
          break;
        case 'artworks':
          aValue = a.artwork_count;
          bValue = b.artwork_count;
          break;
        case 'collections':
          aValue = a.collections;
          bValue = b.collections;
          break;
        case 'joined':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          aValue = a.nickname.toLowerCase();
          bValue = b.nickname.toLowerCase();
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

  // Infinite scroll - show more as user scrolls
  const displayedArtists = filteredArtists.slice(0, displayedCount);
  const hasMore = displayedCount < filteredArtists.length;

  // Reset displayed count when filter/sort changes
  useEffect(() => {
    setDisplayedCount(20);
  }, [searchQuery, sortBy, sortOrder]);

  // Infinite scroll observer
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !loadingMore) {
      setLoadingMore(true);
      setTimeout(() => {
        setDisplayedCount(prev => Math.min(prev + 20, filteredArtists.length));
        setLoadingMore(false);
      }, 300);
    }
  }, [hasMore, loadingMore, filteredArtists.length]);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [handleObserver]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
  };

  const ArtistRow = ({ artist }) => (
    <div className={`artist-row ${!artist.isVibrantCastUser ? 'external-only' : ''}`}>
      <div className="artist-row-main">
        <div className="artist-avatar-wrapper small">
          {artist.profile_img ? (
            <img src={artist.profile_img} alt={artist.nickname} className="artist-avatar" />
          ) : (
            <div className="artist-avatar-placeholder">
              {artist.nickname.substring(0, 2).toUpperCase()}
            </div>
          )}
        </div>
        <div className="artist-info">
          <h3 className="artist-name">
            {artist.nickname}
          </h3>
          {artist.handle && <p className="artist-handle">@{artist.handle}</p>}
        </div>
      </div>
      
      <div className="artist-row-stats">
        <div className="row-stat">
          <Palette size={14} />
          <span>{artist.artwork_count}</span>
        </div>
        <div className="row-stat">
          <Layers size={14} />
          <span>{artist.collections}</span>
        </div>
      </div>

      <div className="artist-row-meta">
        {!artist.isVibrantCastUser ? (
          <span className="external-tag">Not on VC</span>
        ) : (
          <>
            <Clock size={14} />
            <span>{formatDate(artist.updated_at)}</span>
          </>
        )}
      </div>
    </div>
  );

  // Handle artist click - only navigate for VibrantCast users
  const handleArtistClick = (artist) => {
    if (artist.isVibrantCastUser) {
      navigate(`/curator/artists/${artist.uuid}`);
    } else {
      // For external members, could open an invite modal or contact detail
      console.log('External member clicked:', artist);
    }
  };

  if (loading) {
    return (
      <div className="curator-artists-management">
        <div className="loading-state">
          <RefreshCw className="loading-spinner" size={32} />
          <p>Loading artists...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="curator-artists-management">
        <div className="error-state">
          <p>Error loading artists: {error}</p>
          <button onClick={loadArtists} className="retry-btn">
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="curator-artists-management">
      {/* Page Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', margin: '0 0 0.5rem 0' }}>
          Artists
        </h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.95rem' }}>
          Manage {stats.total} artists across {stats.totalCollections} collections.
        </p>
      </div>

      {/* Condensed Controls Bar */}
      <div className="artists-controls-compact">
        <div className="search-container">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search artists by name, email, or handle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="compact-controls">
          <div style={{ position: 'relative' }}>
            <Filter size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', pointerEvents: 'none' }} />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-btn"
              style={{ 
                appearance: 'none', 
                paddingLeft: '32px', 
                paddingRight: '32px',
                height: '36px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="artworks">Sort by Artworks</option>
              <option value="collections">Sort by Collections</option>
              <option value="joined">Sort by Date Joined</option>
            </select>
            <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}>
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            style={{ height: '36px', width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <ArrowUpDown size={16} />
          </button>
        </div>
      </div>

      {/* Artists List */}
      {filteredArtists.length === 0 ? (
        <div className="empty-state">
          <p>No artists found</p>
          {searchQuery && <p className="empty-subtitle">Try adjusting your search terms</p>}
        </div>
      ) : (
        <>
          <div className="artists-list-view">
            {displayedArtists.map((artist) => (
              <div 
                key={artist.uuid}
                onClick={() => handleArtistClick(artist)}
                className={`artist-row-wrapper ${!artist.isVibrantCastUser ? 'non-clickable' : ''}`}
              >
                <ArtistRow artist={artist} />
              </div>
            ))}
          </div>

          {/* Infinite Scroll Loader */}
          {hasMore && (
            <div ref={loaderRef} className="infinite-scroll-loader">
              {loadingMore && (
                <div className="loading-more">
                  <Loader size={20} className="loading-spinner" />
                  <span>Loading more artists...</span>
                </div>
              )}
            </div>
          )}

          {/* End of list indicator */}
          {!hasMore && filteredArtists.length > 20 && (
            <div className="end-of-list">
              <span>Showing all {filteredArtists.length} artists</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CuratorArtistsManagement;
